/**
 * One player's progress as a short code, for a QR code or a save picture.
 *
 * The full save code (Settings) carries every player and all their history,
 * which can be several times too big for a QR code. This carries just one
 * player, packed tight: each level is a short list of numbers, and as much
 * recent history as still fits. Loading it updates or adds that one player
 * and leaves anyone else on the device alone.
 *
 * Pure: no Phaser.
 */
import { deflateSync, inflateSync } from 'fflate';
import { checksum, coerceProfile, extractSaveCode, RUN_HISTORY_LIMIT } from './SaveManager';
import type { ProfileSlot, RunEntry } from './SaveManager';
import { COSMETIC_MILESTONES } from './TideRun';

export const PROFILE_CODE_PREFIX = 'QJP.';
const PROFILE_CODE_VERSION = 1;

/** What a QR code can carry as a link, leaving room for the site address. */
export const QR_CODE_LIMIT = 2200;

type LevelRow = [
  level: number,
  completed: 0 | 1,
  stars: number,
  score: number,
  time: number,
  wpm: number,
  accuracy: number,
  // Whole seconds x4 + stars, and only once the level has a Tide Run clear,
  // so older codes still read. One number per level keeps the QR code small.
  tide?: number,
];
type RunRow = [at: number, level: number, wpm: number, accuracy: number, stars: number];

interface Packed {
  n: string;
  c: string;
  a: string;
  k: Record<string, number>;
  s: ProfileSlot['settings'];
  l: LevelRow[];
  t: [stars: number, score: number, words: number, levels: number, playSec: number];
  u: string[];
  h: RunRow[];
  w: Array<[string, number]>;
  m: Array<[string, number]>;
  /** Tide Run records: runs, furthest, most in a run, best WPM. Absent in older codes. */
  r?: [runs: number, furthest: number, most: number, wpm: number];
  /** Unlocked cosmetics, one bit each in COSMETIC_MILESTONES order. Absent in older codes. */
  x?: number;
  /** School grade, for the player's word ramp. Absent means not sure, or an older code. */
  g?: number;
}

const tenth = (value: number): number => Math.round(value * 10) / 10;

function pack(profile: ProfileSlot, historyCount: number, wordCount: number): Packed {
  // slice(-0) is the whole array, so "no history" has to be said outright.
  const history = historyCount <= 0 ? [] : profile.history.slice(-historyCount);
  const topWords = Object.entries(profile.missedWords)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, wordCount);
  return {
    n: profile.name,
    c: profile.createdAt,
    a: profile.selectedAnimal,
    k: profile.colorPerAnimal as Record<string, number>,
    s: profile.settings,
    l: Object.entries(profile.levels).map(([key, r]): LevelRow => {
      const row: LevelRow = [
        Number(key),
        r.completed ? 1 : 0,
        r.bestStars,
        Math.round(r.bestScore),
        tenth(r.bestTimeSec),
        tenth(r.bestWpm),
        tenth(r.bestAccuracy),
      ];
      if (r.tideBestTimeSec > 0) {
        row.push(Math.max(1, Math.round(r.tideBestTimeSec)) * 4 + Math.min(3, r.tideStars));
      }
      return row;
    }),
    t: [
      profile.totals.stars,
      profile.totals.score,
      profile.totals.wordsTyped,
      profile.totals.levelsCompleted,
      Math.round(profile.totals.playMs / 1000),
    ],
    u: profile.unlockedAnimals,
    // Times as whole minutes since 2020, which is plenty for a progress chart.
    h: history.map((run) => [
      Math.round((Date.parse(run.at) - Date.UTC(2020, 0, 1)) / 60000),
      run.level,
      tenth(run.wpm),
      tenth(run.accuracy),
      run.stars,
    ]),
    w: topWords,
    m: Object.entries(profile.missedKeys),
    r: [
      profile.tide.runs,
      profile.tide.furthestLevel,
      profile.tide.mostLevelsInRun,
      tenth(profile.tide.bestRunWpm),
    ],
    x: COSMETIC_MILESTONES.reduce(
      (bits, m, i) => (profile.unlockedCosmetics.includes(m.id) ? bits | (1 << i) : bits),
      0,
    ),
    ...(profile.grade === null ? {} : { g: profile.grade }),
  };
}

function unpack(p: Packed): unknown {
  const levels: Record<number, unknown> = {};
  for (const [level, completed, stars, score, time, wpm, accuracy, tide] of p.l) {
    levels[level] = {
      tideBestTimeSec: tide === undefined ? 0 : Math.floor(tide / 4),
      tideStars: tide === undefined ? 0 : tide % 4,
      completed: completed === 1,
      bestStars: stars,
      bestScore: score,
      bestTimeSec: time,
      bestWpm: wpm,
      bestAccuracy: accuracy,
    };
  }
  const runs: RunEntry[] = p.h.map(([minutes, level, wpm, accuracy, stars]) => ({
    at: new Date(Date.UTC(2020, 0, 1) + minutes * 60000).toISOString(),
    level,
    wpm,
    accuracy,
    stars,
  }));
  return {
    name: p.n,
    createdAt: p.c,
    selectedAnimal: p.a,
    colorPerAnimal: p.k,
    timedMode: false,
    settings: p.s,
    levels,
    totals: {
      stars: p.t[0],
      score: p.t[1],
      wordsTyped: p.t[2],
      levelsCompleted: p.t[3],
      playMs: p.t[4] * 1000,
    },
    unlockedAnimals: p.u,
    history: runs,
    missedWords: Object.fromEntries(p.w),
    missedKeys: Object.fromEntries(p.m),
    tide:
      p.r === undefined
        ? undefined
        : { runs: p.r[0], furthestLevel: p.r[1], mostLevelsInRun: p.r[2], bestRunWpm: p.r[3] },
    grade: p.g ?? null,
    // A player brought in from a code has already met the helpers.
    seenHelpers: true,
    unlockedCosmetics: COSMETIC_MILESTONES.filter((_, i) => ((p.x ?? 0) & (1 << i)) !== 0).map(
      (m) => m.id,
    ),
  };
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const plain = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(plain + '='.repeat((4 - (plain.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function encodePacked(packed: Packed): string {
  const data = JSON.stringify(packed);
  const envelope = JSON.stringify({ v: PROFILE_CODE_VERSION, sum: checksum(data), data });
  return (
    PROFILE_CODE_PREFIX + toBase64Url(deflateSync(new TextEncoder().encode(envelope), { level: 9 }))
  );
}

export interface ProfileCode {
  readonly code: string;
  /** How many recent runs made it in, for the "includes your last N games" note. */
  readonly runs: number;
}

/**
 * The player's code, as complete as fits in a QR code: all progress always,
 * then as much recent history and missed-word data as there is room for.
 */
export function encodeProfileCode(profile: ProfileSlot, limit = QR_CODE_LIMIT): ProfileCode {
  const historySteps = [RUN_HISTORY_LIMIT, 100, 50, 20, 10, 5, 0];
  const wordSteps = [50, 20, 0];
  let fallback: ProfileCode | null = null;
  for (const runs of historySteps) {
    for (const words of wordSteps) {
      const code = encodePacked(pack(profile, runs, words));
      const kept = Math.min(runs, profile.history.length);
      if (code.length <= limit) return { code, runs: kept };
      fallback = { code, runs: kept };
    }
  }
  // Progress alone always fits in practice (a fully played profile is about
  // 1.3 KB); if a future change ever broke that, the code still works as text.
  return fallback ?? { code: encodePacked(pack(profile, 0, 0)), runs: 0 };
}

export type ProfileCodeResult =
  | { ok: true; profile: ProfileSlot }
  | { ok: false; reason: 'unreadable' | 'checksum' | 'version' | 'shape' };

export function isProfileCode(text: string): boolean {
  return extractSaveCode(text).startsWith(PROFILE_CODE_PREFIX);
}

export function decodeProfileCode(input: string): ProfileCodeResult {
  const code = extractSaveCode(input);
  if (!code.startsWith(PROFILE_CODE_PREFIX)) return { ok: false, reason: 'unreadable' };
  let envelope: unknown;
  try {
    const bytes = inflateSync(fromBase64Url(code.slice(PROFILE_CODE_PREFIX.length)));
    envelope = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return { ok: false, reason: 'unreadable' };
  }
  if (typeof envelope !== 'object' || envelope === null) return { ok: false, reason: 'shape' };
  const { v, sum, data } = envelope as { v?: unknown; sum?: unknown; data?: unknown };
  if (typeof data !== 'string' || typeof sum !== 'string') return { ok: false, reason: 'shape' };
  if (typeof v !== 'number' || v > PROFILE_CODE_VERSION) return { ok: false, reason: 'version' };
  if (checksum(data) !== sum) return { ok: false, reason: 'checksum' };
  try {
    const profile = coerceProfile(unpack(JSON.parse(data) as Packed));
    return profile === null ? { ok: false, reason: 'shape' } : { ok: true, profile };
  } catch {
    return { ok: false, reason: 'shape' };
  }
}
